import React, { useState, useEffect, useMemo } from "react";
import "./floorsList.css";
import API from "src/API2";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcLayer = () => <Svg size={14}><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></Svg>;
const IcClose = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcBed   = () => <Svg size={14}><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;
const IcUser  = () => <Svg size={14}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></Svg>;
const IcPlus  = () => <Svg><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;

const isRoomOccupied = (reservations) => {
  const now = new Date();
  return (reservations || []).some((r) => {
    const ci = new Date(r.checkin_date);
    const co = new Date(r.checkout_date);
    return now >= ci && now <= co;
  });
};

const filterActiveReservations = (reservations) => {
  const now = new Date();
  return (reservations || []).filter((r) => now <= new Date(r.checkout_date));
};

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};

const FloorsList = ({ itera }) => {
  const [floors, setFloors] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState("all");
  const [selectedRoomType, setSelectedRoomType] = useState("all");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedRoomReservations, setSelectedRoomReservations] = useState([]);
  const [activeTab, setActiveTab] = useState("room");

  /* ── Load floors + room types ── */
  useEffect(() => {
    API.get("floors")
      .then((res) => {
        const sorted = (res.data.floors || []).sort((a, b) => a.level - b.level);
        setFloors(sorted);
      })
      .catch((e) => console.error("Error fetching floors:", e));

    API.get("rooms")
      .then((res) => setRoomTypes(res.data.rooms || []))
      .catch((e) => console.error("Error fetching room types:", e));
  }, [itera]);

  /* ── Filter helper ── */
  const filterRooms = (rooms) => {
    let out = rooms || [];

    if (availabilityFilter === "available") {
      out = out.filter((r) => r.available === true && !isRoomOccupied(r.hotelReservation));
    } else if (availabilityFilter === "occupied") {
      out = out.filter((r) => isRoomOccupied(r.hotelReservation));
    } else if (availabilityFilter === "off") {
      out = out.filter((r) => r.available === false);
    }

    if (selectedRoomType !== "all") {
      out = out.filter((r) => r.hotelRoom_id === selectedRoomType);
    }

    return out.slice().sort((a, b) => a.number - b.number);
  };

  const handleRoomClick = async (roomId) => {
    try {
      const response = await API.get(`roomitems/${roomId}`);
      const active = filterActiveReservations(response.data.room.hotelReservation || []);
      setSelectedRoom(response.data.room);
      setSelectedRoomReservations(active);
      setActiveTab("room");
    } catch (e) {
      console.error("Error fetching room details:", e);
    }
  };

  const closeOverlay = () => {
    setSelectedRoom(null);
    setSelectedRoomReservations([]);
  };

  /* ── Aggregate stats per floor ── */
  const floorStats = useMemo(() => {
    const map = {};
    floors.forEach((f) => {
      const total = (f.rooms || []).length;
      const occupied = (f.rooms || []).filter((r) => isRoomOccupied(r.hotelReservation)).length;
      const off = (f.rooms || []).filter((r) => r.available === false).length;
      const free = total - occupied - off;
      map[f.level] = { total, occupied, free, off };
    });
    return map;
  }, [floors]);

  /* ── Top-level totals (for the legend) ── */
  const totals = useMemo(() => {
    const acc = { total: 0, free: 0, occupied: 0, off: 0 };
    floors.forEach((f) => {
      const s = floorStats[f.level] || { total: 0, free: 0, occupied: 0, off: 0 };
      acc.total += s.total;
      acc.free += s.free;
      acc.occupied += s.occupied;
      acc.off += s.off;
    });
    return acc;
  }, [floors, floorStats]);

  const visibleFloors = floors.filter((f) =>
    selectedFloor === "all" || Number(selectedFloor) === f.level,
  );

  return (
    <div className="fl-list">
      {/* ── Filters + legend ── */}
      <div className="fl-toolbar">
        <div className="fl-filters">
          <label className="fl-select">
            <span>Floor</span>
            <select value={selectedFloor} onChange={(e) => setSelectedFloor(e.target.value)}>
              <option value="all">All floors</option>
              {floors.map((floor) => (
                <option key={floor.level} value={floor.level}>
                  Floor {floor.level}
                </option>
              ))}
            </select>
          </label>
          <label className="fl-select">
            <span>Room type</span>
            <select value={selectedRoomType} onChange={(e) => setSelectedRoomType(e.target.value)}>
              <option value="all">All types</option>
              {roomTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.code}</option>
              ))}
            </select>
          </label>
          <label className="fl-select">
            <span>Status</span>
            <select value={availabilityFilter} onChange={(e) => setAvailabilityFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="available">Available now</option>
              <option value="occupied">Occupied</option>
              <option value="off">Off market</option>
            </select>
          </label>
        </div>
        <div className="fl-legend">
          <span className="fl-legend__item"><span className="fl-dot fl-dot--free" /> Free <strong>{totals.free}</strong></span>
          <span className="fl-legend__item"><span className="fl-dot fl-dot--busy" /> Occupied <strong>{totals.occupied}</strong></span>
          <span className="fl-legend__item"><span className="fl-dot fl-dot--off" /> Off market <strong>{totals.off}</strong></span>
        </div>
      </div>

      {/* ── Floors stack ── */}
      {floors.length === 0 ? (
        <div className="fl-empty">
          <IcLayer />
          <h3>No floors yet</h3>
          <p>Add a floor to start mapping your property.</p>
        </div>
      ) : (
        <div className="fl-stack">
          {visibleFloors.slice().reverse().map((floor) => {
            const filtered = filterRooms(floor.rooms);
            const stats = floorStats[floor.level] || { total: 0, free: 0, occupied: 0, off: 0 };
            return (
              <article key={floor.level} className="fl-floor">
                <header className="fl-floor__head">
                  <div className="fl-floor__title">
                    <span className="fl-floor__num">{floor.level}</span>
                    <div>
                      <h3>Floor {floor.level}</h3>
                      <span className="fl-floor__sub">{stats.total} room{stats.total !== 1 ? "s" : ""}</span>
                    </div>
                  </div>
                  <div className="fl-floor__stats">
                    <span className="fl-mini"><span className="fl-dot fl-dot--free" />{stats.free}</span>
                    <span className="fl-mini"><span className="fl-dot fl-dot--busy" />{stats.occupied}</span>
                    <span className="fl-mini"><span className="fl-dot fl-dot--off" />{stats.off}</span>
                  </div>
                </header>
                {filtered.length === 0 ? (
                  <div className="fl-floor__empty">No rooms match your filters on this floor.</div>
                ) : (
                  <div className="fl-rooms">
                    {filtered.map((room) => {
                      const occupied = isRoomOccupied(room.hotelReservation);
                      const off = room.available === false;
                      const cls = off ? "is-off" : occupied ? "is-busy" : "is-free";
                      return (
                        <button
                          key={room.id}
                          type="button"
                          className={`fl-room ${cls}`}
                          onClick={() => handleRoomClick(room.id)}
                          title={`Room ${room.number} · ${off ? "Off market" : occupied ? "Occupied" : "Available"}`}
                        >
                          <span className="fl-room__num">{room.number}</span>
                          <span className="fl-room__type">{room.hotelRoom?.code || ""}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* ── Detail drawer ── */}
      {selectedRoom && (
        <div className="fl-drawer-scrim" onClick={closeOverlay}>
          <aside
            className="fl-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Room details"
          >
            <header className="fl-drawer__head">
              <div>
                <span className="fl-drawer__eyebrow">Room {selectedRoom.number}</span>
                <h2>{selectedRoom.hotelRoom?.code || "—"}</h2>
                <span className={`fl-status fl-status--${selectedRoom.available ? "on" : "off"}`}>
                  {selectedRoom.available ? "Available" : "Off market"}
                </span>
              </div>
              <button className="fl-icon-btn" onClick={closeOverlay} aria-label="Close">
                <IcClose />
              </button>
            </header>

            <div className="fl-drawer__tabs">
              <button
                className={`fl-tab ${activeTab === "room" ? "is-active" : ""}`}
                onClick={() => setActiveTab("room")}
              >
                Details
              </button>
              <button
                className={`fl-tab ${activeTab === "reservations" ? "is-active" : ""}`}
                onClick={() => setActiveTab("reservations")}
              >
                Reservations
                {selectedRoomReservations.length > 0 && (
                  <span className="fl-tab__badge">{selectedRoomReservations.length}</span>
                )}
              </button>
            </div>

            <div className="fl-drawer__body">
              {activeTab === "room" && (
                <section className="fl-detail">
                  <div className="fl-detail-row">
                    <span><IcBed /> Type</span>
                    <strong>{selectedRoom.hotelRoom?.code || "—"}</strong>
                  </div>
                  <div className="fl-detail-row">
                    <span><IcLayer /> Floor</span>
                    <strong>Floor {selectedRoom.floor?.level ?? "—"}</strong>
                  </div>
                  <div className="fl-detail-row">
                    <span>Status</span>
                    <strong>{selectedRoom.available ? "Available" : "Off market"}</strong>
                  </div>
                  {selectedRoom.hotelRoom?.description && (
                    <div className="fl-detail-block">
                      <h4>Description</h4>
                      <p>{selectedRoom.hotelRoom.description}</p>
                    </div>
                  )}
                </section>
              )}

              {activeTab === "reservations" && (
                <section className="fl-reservations">
                  {selectedRoomReservations.length > 0 ? (
                    <ul className="fl-resv-list">
                      {selectedRoomReservations.map((r) => {
                        const u = r.user?.[0] || {};
                        return (
                          <li key={r.id} className="fl-resv">
                            <div className="fl-resv__avatar">
                              {((u.firstname?.[0] || "?") + (u.lastname?.[0] || "")).toUpperCase()}
                            </div>
                            <div className="fl-resv__body">
                              <strong>
                                <IcUser /> {u.firstname || "—"} {u.lastname || ""}
                              </strong>
                              <span>
                                {fmtDate(r.checkin_date)} → {fmtDate(r.checkout_date)}
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="fl-resv-empty">No active reservations.</div>
                  )}
                  <button
                    type="button"
                    className="fl-btn fl-btn--primary"
                    onClick={() => (window.location.href = "/reservations/create")}
                  >
                    <IcPlus /> New reservation
                  </button>
                </section>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default FloorsList;
